// DateRangePickerComponent.js

import React, { useState, useEffect } from "react";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css"; // main css file
import "react-date-range/dist/theme/default.css"; // theme css file
import Calendar from "../../assets/Calendar.svg";
import styles from "./style.module.css";

import { enGB } from "date-fns/locale";

const DatePicker = ({
  initialDate,
  initialStartDate,
  initialEndDate,
  onDatesChange,
  keepEmpty = false,
  onlyIcon = false,
  visableIcon = false,
  mondayOnly = false,
  inputHeight = "24px", // default height
  borderThickness = "1px", // default border thickness
  borderColor = "#3C96D4", // default border color
  oppCard = false,
  disabled = false,
  showDateDisplay = false,
  // When true, the calendar stays open after a selection (useful for picking ranges)
  keepOpenOnSelect = false,
  // When enabled, shows quick actions for special billing categories beneath the calendar
  enableBillingCategories = false,
}) => {
  const [state, setState] = useState([]);
  console.log(disabled, "disableddisabled");
  useEffect(() => {
    let parsedDate;

    if (keepEmpty) {
      parsedDate = null;  // Keep the date empty if keepEmpty is true
    } else {
      if (initialStartDate || initialEndDate) {
        // If explicit range is provided (dd/mm/yyyy), prefer it
        const parseDmy = (str) => {
          if (!str || typeof str !== 'string' || !str.includes('/')) return null;
          const parts = str.split('/');
          return new Date(parts[2], parts[1] - 1, parts[0]);
        };
        const start = parseDmy(initialStartDate) || new Date();
        const end = parseDmy(initialEndDate) || start;
        const dates = [{ startDate: start, endDate: end, key: 'selection' }];
        setState(dates);
        return;
      } else if (initialDate instanceof Date && !isNaN(initialDate)) {
        // If initialDate is already a valid Date object, use it directly
        parsedDate = initialDate;
      } else if (typeof initialDate === 'string') {
        // Prefer explicit dd/mm/yyyy parsing when slashes are present to avoid US locale ambiguity
        if (initialDate.includes('/')) {
          const [dd, mm, yyyy] = initialDate.split('/');
          const candidate = new Date(`${yyyy}-${mm}-${dd}`);
          if (!isNaN(candidate.getTime())) {
            parsedDate = candidate;
          } else {
            console.warn("DatePicker received an invalid DD/MM/YYYY date string:", initialDate);
            parsedDate = new Date();
          }
        } else {
          // Otherwise try ISO or other Date-parsable formats
          const isoCandidate = new Date(initialDate);
          if (!isNaN(isoCandidate.getTime())) {
            parsedDate = isoCandidate;
          } else {
            console.warn("DatePicker received an unexpected initialDate string:", initialDate);
            parsedDate = new Date();
          }
        }
      } else {
        // Fallback to the current date if initialDate is null, undefined, empty, or an unexpected format
        if (initialDate && typeof initialDate !== 'string') {
          console.warn("DatePicker received an unexpected initialDate format:", initialDate);
        }
        parsedDate = new Date();
      }
    }

    // Construct the dates object for the DatePicker
    const dates = [
      {
        startDate: parsedDate,
        endDate: parsedDate, // Assuming single date selection for now
        key: "selection",
      },
    ];

    setState(dates);
  }, [initialDate, initialStartDate, initialEndDate, keepEmpty]);

  const [isOpen, setIsOpen] = useState(false);
  const [hideInputFields, setHideInputFields] = useState(false);
  const [selectionStep, setSelectionStep] = useState(0); // 0: pick start, 1: pick end

  useEffect(() => {
    setHideInputFields(!showDateDisplay);
  }, [showDateDisplay]);

  // Apply styles to hide input fields when the component opens
  useEffect(() => {
    if (isOpen && hideInputFields) {
      // Small delay to ensure the DateRangePicker has rendered
      const timer = setTimeout(() => {
        const styleElement = document.getElementById('hide-date-inputs-style');
        if (!styleElement) {
          const style = document.createElement('style');
          style.id = 'hide-date-inputs-style';
          style.innerHTML = `
            .rdrDateDisplayWrapper {
              display: none !important;
            }
            .rdrDateInput {
              display: none !important;
            }
            .rdrDateDisplayItem {
              display: none !important;
            }
            .rdrDateDisplayItemActive {
              display: none !important;
            }
            .rdrDateDisplay {
              display: none !important;
            }
          `;
          document.head.appendChild(style);
        }
      }, 10);
      
      return () => {
        clearTimeout(timer);
        // Clean up the style when component unmounts or closes
        if (!isOpen) {
          const styleElement = document.getElementById('hide-date-inputs-style');
          if (styleElement) {
            styleElement.remove();
          }
        }
      };
    }
  }, [isOpen, hideInputFields]);

  const handleRangeChange = (item) => {
    console.log("[item.selection])", [item.selection]);

    // If mondayOnly is true, only process changes for Mondays
    if (mondayOnly && !isMonday(item.selection.startDate)) {
      return;
    }

    setState([item.selection]);

    const localStartDate = new Date(
      item.selection.startDate.getTime() -
      item.selection.startDate.getTimezoneOffset() * 60000
    ).toISOString();
    const localEndDate = new Date(
      item.selection.endDate.getTime() -
      item.selection.endDate.getTimezoneOffset() * 60000
    ).toISOString();

    const isFinal = !keepOpenOnSelect || selectionStep === 1;

    onDatesChange({
      startDate: localStartDate,
      endDate: localEndDate,
      isFinalSelection: isFinal,
    });

    if (keepOpenOnSelect) {
      if (selectionStep === 0) {
        setSelectionStep(1);
      } else {
        setSelectionStep(0);
        setIsOpen(false);
      }
    } else {
      setIsOpen(false);
    }
  };

  // Helper to get last day of a month
  const getLastDayOfMonth = (year, monthIndexZeroBased) => {
    // Create date for day 0 of next month which gives us the last day of current month
    return new Date(year, monthIndexZeroBased + 1, 0);
  };

  // Special billing categories encoded as sentinel dates far in the future
  // Note: We intentionally use year 2099 to avoid collisions with real billing dates
  const specialBillingCategories = [
    {
      key: "UNSCHEDULED",
      label: "Unscheduled",
      date: getLastDayOfMonth(2099, 10), // November 2099
    },
    {
      key: "ON_HOLD",
      label: "On Hold",
      date: getLastDayOfMonth(2099, 11), // December 2099
    },
  ];

  const handleSpecialCategoryClick = (category) => {
    const selection = {
      startDate: category.date,
      endDate: category.date,
      key: "selection",
    };
    setState([selection]);

    const localStartDate = new Date(
      selection.startDate.getTime() -
      selection.startDate.getTimezoneOffset() * 60000
    ).toISOString();
    const localEndDate = new Date(
      selection.endDate.getTime() -
      selection.endDate.getTimezoneOffset() * 60000
    ).toISOString();

    onDatesChange({
      startDate: localStartDate,
      endDate: localEndDate,
    });

    setIsOpen(false);
  };

  // Function to format the date or month based on mondayOnly
  const formatDateOrMonth = (date) => {
    // Read values directly from the Date object to avoid locale-dependent parsing
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    // Render friendly labels for special billing categories
    if (year === 2099 && month === 11) {
      return "Unscheduled";
    }
    if (year === 2099 && month === 12) {
      return "On Hold";
    }

    // If mondayOnly is true, format to not show the date
    if (mondayOnly) {
      return ``;
    } else {
      // UK format with zero padding: DD/MM/YYYY
      return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
    }
  };

  // Function to check if a date is Monday
  const isMonday = (date) => {
    return date.getDay() === 1;
  };

  // Function to disable non-Monday dates if mondayOnly is true
  const dayBlocker = (date) => {
    return mondayOnly && !isMonday(date);
  };

  return (
    <>
      {onlyIcon ? (
        <div>
          {oppCard ? (
            <img
              src={Calendar}
              alt="CalendarIcon"
              onClick={!disabled ? () => setIsOpen(true) : undefined}
              className={styles.cardCalendarIcon}
              style={{
                position: "relative",
                filter: disabled ? 'grayscale(100%) opacity(0.5)' : 'none',
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            />
          ) : (
            <img
              src={Calendar}
              alt="CalendarIcon"
              onClick={!disabled ? () => setIsOpen(true) : undefined}
              className={styles.CalendarIcon}
              style={{
                position: "relative",
                filter: disabled ? 'grayscale(100%) opacity(0.5)' : 'none',
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            />
          )}
        </div>
      ) : (
        <div className={styles.datePicker}>
          <div className={styles.inputContainer}>
            <input
              className={styles.inputDateLeaveProfile}
              onClick={!disabled ? () => setIsOpen(true) : undefined}
              value={
                state[0]?.startDate && state[0]?.endDate
                  ? formatDateOrMonth(state[0].startDate)
                  : keepEmpty
                    ? "No Date Selected"
                    : ""
              }
              readOnly
              style={{
                height: inputHeight,
                border: `${borderThickness} solid ${borderColor}`,
                backgroundColor: disabled ? 'lightgray' : '#fff',
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            />
            {visableIcon && (
              <div
                className={styles.iconContainer}
                style={{
                  position: "absolute",
                  left: '100%',
                  top: "60%",
                  zIndex: "99",
                  transform: "translateY(-50%)",
                }}>
                <img
                  className={styles.calendarIcon}
                  src={Calendar}
                  alt="CalendarIcon"
                  onClick={!disabled ? () => setIsOpen(true) : undefined}
                  style={{
                    marginTop: mondayOnly ? "5px" : "3px",
                    filter: disabled
                      ? 'grayscale(100%) opacity(0.5)'
                      : mondayOnly
                        ? "brightness(0) saturate(100%) invert(54%) sepia(81%) saturate(1112%) hue-rotate(178deg) brightness(89%) contrast(85%)"
                        : "none",
                    cursor: disabled ? 'not-allowed' : 'pointer',
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* DatePicker modal */}
      {isOpen && !disabled && (
        <div
          onClick={() => setIsOpen(false)}
          className={styles.backgroundOfDatePicker}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={styles.datePickerDiv}
          >
            <DateRangePicker
              onChange={handleRangeChange}
              showSelectionPreview={true}
              moveRangeOnFirstSelection={false}
              months={1}
              ranges={state}
              shownDate={(function(){
                const d = state?.[0]?.startDate;
                if (!d) return new Date();
                const year = d.getFullYear();
                const month = d.getMonth() + 1;
                // If a special category is selected, default the visible month back to current month
                if (year === 2099 && (month === 11 || month === 12)) {
                  return new Date();
                }
                return d;
              })()}
              direction="horizontal"
              staticRanges={[]}
              disabledDay={dayBlocker}
              firstDayOfWeek={1}
              locale={enGB}
            />
            {enableBillingCategories && (
              <div style={{ borderTop: '1px solid #e5e5e5', padding: '8px 12px' }}>
                <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '6px' }}>
                  Quick set to:
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {specialBillingCategories.map((cat) => (
                    <button
                      key={cat.key}
                      onClick={() => handleSpecialCategoryClick(cat)}
                      style={{
                        fontSize: '12px',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        border: `1px solid ${borderColor}`,
                        background: '#fff',
                        cursor: 'pointer',
                      }}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default DatePicker;
